import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";

/**
 * Garde anti-crash React + extensions qui modifient le DOM (Google Traduction
 * surtout) : ces extensions enveloppent/déplacent les nœuds texte, puis React
 * tente removeChild/insertBefore sur un nœud qui n'est plus son enfant et lève
 * « The node to be removed is not a child of this node » → écran d'erreur.
 * On rend ces deux opérations tolérantes au lieu de laisser planter l'app.
 * (Le décompte 4·3·2·1 de l'intro change le texte rapidement → déclencheur).
 */
if (typeof Node === "function" && Node.prototype) {
  const origRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (this: Node, child: any) {
    if (child && child.parentNode !== this) {
      return child;
    }
    return (origRemoveChild as any).call(this, child);
  } as typeof Node.prototype.removeChild;

  const origInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (this: Node, newNode: any, referenceNode: any) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }
    return (origInsertBefore as any).call(this, newNode, referenceNode);
  } as typeof Node.prototype.insertBefore;
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;
  if (!isUnauthorized) return;

  // Ne rediriger que depuis les pages admin.
  // Les pages publiques (accueil, blog, tarifs, villes, etc.) ne doivent
  // jamais forcer la connexion - les visiteurs non connectés doivent
  // pouvoir naviguer librement sur tout le site.
  const isAdminPage = window.location.pathname.startsWith("/admin");
  if (!isAdminPage) return;

  // Rediriger vers la page de login admin locale (pas Manus OAuth)
  window.location.href = "/admin/login";
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    // Ne pas logger les erreurs UNAUTHED sur les pages publiques (bruit normal)
    const isUnauthed = error instanceof TRPCClientError && error.message === UNAUTHED_ERR_MSG;
    if (!isUnauthed) {
      console.error("[API Query Error]", error);
    }
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    const isUnauthed = error instanceof TRPCClientError && error.message === UNAUTHED_ERR_MSG;
    if (!isUnauthed) {
      console.error("[API Mutation Error]", error);
    }
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
